import {
  ChartBar,
  CHART_DATE_FORMATS_FOR_PICKER,
  useValidActiveChartRangeType
} from "components";
import { abbreviateNumber } from "helpers";
import { t } from "i18next";
import { BoxShadowLayout } from "layouts";
import { Stat } from "ui";

import { StatisticChartDatePicker } from "./atoms";
import { StatisticsChartProps } from "./StatisticsChart.types";

import styles from "./StatisticsChart.module.scss";

const StatisticsChart = ({
  totalTitle,
  withComparativePeriod,
  setWithComparativePeriod,
  comparablePeriodTitle = "In comparable period",
  withPercentageInComparablePeriod = true,
  customPercentageInComparablePeriodValue,
  titleValue = 0,
  comparablePeriodValue = 0,
  secondaryTitle,
  secondaryValue = 0,
  isLoadingSecondary = false,
  children,
  dateRange,
  setDateRange,
  dateRangeForCompare,
  setDateRangeForCompare,
  withPeriodControls = true,
  chartData,
  isLoading = false,
  isLoadingForCompare = false
}: StatisticsChartProps) => {
  const { activeRangeType, setActiveRangeType } =
    useValidActiveChartRangeType();
  return (
    <BoxShadowLayout className={styles.statistics}>
      <div className={styles.statistics__chart}>
        <div className={styles["statistics__chart--amount"]}>
          <Stat
            size="lg"
            title={t(totalTitle)}
            amount={abbreviateNumber(titleValue, 2)}
            isLoadingAmount={isLoading}
            className={styles["statistics__chart--amount-item"]}
          />

          {secondaryTitle && (
            <>
              <div className={styles["statistics__chart--amount-separator"]} />

              <Stat
                size="md"
                title={secondaryTitle}
                amount={abbreviateNumber(secondaryValue, 2)}
                isLoadingAmount={isLoadingSecondary}
                className={styles["statistics__chart--amount-item"]}
              />
            </>
          )}

          {withComparativePeriod && (
            <>
              <div className={styles["statistics__chart--amount-separator"]} />

              <Stat
                size="md"
                title={t(comparablePeriodTitle)}
                amount={comparablePeriodValue.toLocaleString("en-US")}
                isLoadingAmount={isLoadingForCompare}
                percentage={
                  withPercentageInComparablePeriod
                    ? (customPercentageInComparablePeriodValue ??
                      ((comparablePeriodValue - titleValue) / titleValue) * 100)
                    : undefined
                }
                className={styles["statistics__chart--amount-item"]}
              />
            </>
          )}
        </div>

        <ChartBar
          data={chartData}
          dateRange={dateRange}
          activeRangeType={activeRangeType}
          onRangeTypeChange={setActiveRangeType}
          isLoading={isLoading}
          datepicker={
            withPeriodControls && (
              <StatisticChartDatePicker
                value={dateRange}
                onChange={setDateRange}
                valueForCompare={dateRangeForCompare}
                onChangeValueForCompare={setDateRangeForCompare}
                withComparativePeriod={withComparativePeriod}
                setWithComparativePeriod={setWithComparativePeriod}
                activeRangeType={activeRangeType}
                dateFormat={CHART_DATE_FORMATS_FOR_PICKER[activeRangeType]}
              />
            )
          }
        />
      </div>

      {children}
    </BoxShadowLayout>
  );
};

export default StatisticsChart;
